import { useRef } from "react";
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import {
  MailTestApprovalRunResultDto,
  MailTestApprovalState,
  MailTestCatalogDto,
  MailTestItemDto,
  MailTestItemKind,
  MailTestItemsDto,
  MailTestItemsQuery,
  MailTestSamplesDto,
  MailTestSendResultDto,
  MailTestTemplateDto,
  MailTestUsedSamplesDto,
  MailTestVariantDto,
} from "../models/responses/MailTestDto";
import { MailTestSendRequest } from "../models/bodies/MailTestSendRequest";
import { MailTestApprovalRunRequest } from "../models/bodies/MailTestApprovalRunRequest";

// Admin mail test (flag REACT_APP_MAIL_TEST_ENABLED). Admin surface with a
// fixed call budget: the template catalogue is ONE GET on page open (5 min
// stale, no polling, refetch only via the Refresh button), a vendor's
// samples are ONE GET the first time that vendor is chosen (kept for 5 min,
// so switching template/variant/sample costs nothing), and "Preview data" /
// "Send test mail" are ONE POST per click. Both queries run with retry:
// false - the app's QueryClient sets no retry, so react-query's default (3
// retries with back-off) would turn a 404 (API flag off) or a 5xx into FOUR
// calls and a ~7 s wait before the screen can say what happened. The send
// mutation invalidates NOTHING: a test mail changes no data anywhere, so
// there is nothing to re-read.
export interface MailTestRepository {
  useTemplates(options?: { enabled?: boolean }): Resource<MailTestCatalogDto>;
  // Idle until a vendor is chosen (vendorOrgId defined); never refetches on
  // its own afterwards.
  useSamples(
    vendorOrgId: number | undefined,
    options?: { take?: number; enabled?: boolean },
  ): Resource<MailTestSamplesDto>;
  // The Refresh button: re-reads the catalogue and the chosen vendor's
  // samples (one call each) - the samples of other vendors are dropped from
  // the cache so a later pick reads fresh data.
  refresh(vendorOrgId?: number): Promise<void>;
  // One hook instance per screen: see MailTestSend.
  useSend(): MailTestSend;
  // Mail test additions (flag REACT_APP_MAIL_TEST_APPROVAL_RUN_ENABLED).
  // "Find items": idle without a query (undefined) - the screen sets one
  // only when a search settles (300 ms after typing, or Search/Enter); ONE
  // GET ops/mail-test/items per distinct query, a repeated query is a cache
  // hit for 5 min (Search on the same query = refetch()). Never refetches
  // on its own.
  useItems(
    query: MailTestItemsQuery | undefined,
    options?: { enabled?: boolean },
  ): Resource<MailTestItemsDto>;
  // "Real run": see MailTestApprovalRun. One hook instance per screen.
  useApprovalRun(): MailTestApprovalRun;
}

export interface MailTestApprovalRun {
  // ONE POST ops/mail-test/approval-run; resolves with the normalised
  // answer. A call while one is in flight returns the running promise - no
  // second POST (a second wake against the rate limit), whatever the
  // button state was when the click landed.
  run(body: MailTestApprovalRunRequest): Promise<MailTestApprovalRunResultDto>;
  isLoading: boolean;
  data: MailTestApprovalRunResultDto | undefined;
  error: unknown;
  variables: MailTestApprovalRunRequest | undefined;
  reset(): void;
}

export interface MailTestSend {
  // ONE POST ops/mail-test/send; resolves with the normalised answer. A
  // call while one is in flight returns the running promise - no second
  // POST (and no second test mail against the rate limit), whatever the
  // button state was when the click landed.
  send(body: MailTestSendRequest): Promise<MailTestSendResultDto>;
  isLoading: boolean;
  // Any send on the screen (Preview and Send share one mutation key).
  isAnyRunning: boolean;
  // The last answer of this hook (cleared when a new run starts).
  data: MailTestSendResultDto | undefined;
  error: unknown;
  // What the running/last call was (dryRun or a real send), so the screen
  // can label the busy state and the result.
  variables: MailTestSendRequest | undefined;
  reset(): void;
}

const text = (value: unknown): string | null =>
  value === null || value === undefined ? null : String(value);

const flag = (value: unknown): boolean => value === true;

const int = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const ints = (value: unknown): number[] =>
  Array.isArray(value)
    ? value.filter(
        (v): v is number => typeof v === "number" && Number.isFinite(v),
      )
    : [];

const strings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value
        .filter((v) => v !== null && v !== undefined)
        .map((v) => String(v))
    : [];

const record = <T,>(
  value: unknown,
  keep: (v: unknown) => v is T,
): Record<string, T> => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  const out: Record<string, T> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (keep(v)) out[k] = v;
  }
  return out;
};

const isBoolean = (v: unknown): v is boolean => typeof v === "boolean";
const isFiniteNumber = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

// { key: value } when the value is a non-empty string / a finite number,
// {} otherwise - spread into an object to add a field only when present.
const optionalText = (key: string, value: unknown): Record<string, string> => {
  const s = text(value);
  return s === null || s === "" ? {} : { [key]: s };
};

const optionalInt = (key: string, value: unknown): Record<string, number> => {
  const n = int(value);
  return n === null ? {} : { [key]: n };
};

function normalizeUsedSamples(raw: unknown): MailTestUsedSamplesDto {
  const body = (
    raw !== null && typeof raw === "object" ? raw : {}
  ) as Record<string, unknown>;
  return {
    vendorOrgId: int(body.vendorOrgId),
    customerOrgId: int(body.customerOrgId),
    logoIds: ints(body.logoIds),
    designIds: ints(body.designIds),
    orderId: int(body.orderId),
    userId: int(body.userId),
    entryId: int(body.entryId),
  };
}

// The raw JSON of POST ops/mail-test/send made safe for the screen: every
// string nullable, arrays present (missing -> []), booleans false unless
// true, `data` kept as the object the API sent (it IS the template data and
// is shown verbatim) or null. A body that is not an object counts as empty.
export function normalizeMailTestSendResult(
  raw: unknown,
): MailTestSendResultDto {
  const body = (
    raw !== null && typeof raw === "object" ? raw : {}
  ) as Record<string, unknown>;
  const data =
    body.data !== null &&
    typeof body.data === "object" &&
    !Array.isArray(body.data)
      ? (body.data as Record<string, unknown>)
      : null;
  return {
    sent: flag(body.sent),
    dryRun: flag(body.dryRun),
    templateKey: text(body.templateKey) ?? "",
    variant: text(body.variant),
    resolvedVariant: text(body.resolvedVariant),
    kind: text(body.kind) ?? "",
    templateSetting: text(body.templateSetting),
    templateId: text(body.templateId),
    configured: flag(body.configured),
    senderName: text(body.senderName),
    replyTo: text(body.replyTo),
    replyToRule: text(body.replyToRule),
    recipient: text(body.recipient) ?? "",
    realRecipientRule: text(body.realRecipientRule),
    data,
    subject: text(body.subject),
    html: text(body.html),
    categories: strings(body.categories),
    samples: normalizeUsedSamples(body.samples),
    flags: record(body.flags, isBoolean),
    warnings: strings(body.warnings),
    sendGridStatus: int(body.sendGridStatus),
    sendGridError: text(body.sendGridError),
    callBudget:
      body.callBudget === null || body.callBudget === undefined
        ? null
        : record(body.callBudget, isFiniteNumber),
    // feat/mail-i18n: added ONLY when the API sent them, so an answer from
    // an API without the feature normalises to exactly the object above.
    ...optionalText("lang", body.lang),
    ...optionalText("langSource", body.langSource),
    ...optionalInt("tKeyCount", body.tKeyCount),
  };
}

// The catalogue as the screen reads it: templates an array of templates
// whose variants are arrays (missing -> []) with `requires` an array of
// strings. Anything that is not a template object is dropped.
export function normalizeMailTestCatalog(raw: unknown): MailTestCatalogDto {
  const body = (
    raw !== null && typeof raw === "object" ? raw : {}
  ) as Record<string, unknown>;
  const templates: MailTestTemplateDto[] = (
    Array.isArray(body.templates) ? body.templates : []
  )
    .filter((t) => t !== null && typeof t === "object")
    .map((t) => {
      const r = t as Record<string, unknown>;
      const variants: MailTestVariantDto[] = (
        Array.isArray(r.variants) ? r.variants : []
      )
        .filter((v) => v !== null && typeof v === "object")
        .map((v) => {
          const rv = v as Record<string, unknown>;
          return {
            id: text(rv.id) ?? "",
            description: text(rv.description) ?? "",
            sender: text(rv.sender),
            requires: strings(rv.requires),
            dead: flag(rv.dead),
          };
        })
        .filter((v) => v.id !== "");
      return {
        key: text(r.key) ?? "",
        kind: text(r.kind) ?? "template",
        description: text(r.description),
        setting: text(r.setting),
        azureSetting: text(r.azureSetting),
        configured: flag(r.configured),
        fallbackSetting: text(r.fallbackSetting),
        variants,
      };
    })
    .filter((t) => t.key !== "");
  const limits =
    body.limits !== null && typeof body.limits === "object"
      ? {
          maxSendsPerHour:
            int((body.limits as Record<string, unknown>).maxSendsPerHour) ?? 0,
          dryRunSoftCapPerHour:
            int((body.limits as Record<string, unknown>).dryRunSoftCapPerHour) ??
            0,
        }
      : null;
  const excluded = (Array.isArray(body.excluded) ? body.excluded : [])
    .filter((e) => e !== null && typeof e === "object")
    .map((e) => {
      const r = e as Record<string, unknown>;
      return { key: text(r.key) ?? "", reason: text(r.reason) ?? "" };
    });
  return {
    flags: record(body.flags, isBoolean),
    limits,
    templates,
    excluded,
    // feat/mail-i18n: only when the API sent them (an older API has neither;
    // the screen then offers the full language list and lets the API decide).
    ...(typeof body.i18nEnabled === "boolean"
      ? { i18nEnabled: body.i18nEnabled }
      : {}),
    ...(Array.isArray(body.languages)
      ? { languages: strings(body.languages) }
      : {}),
    // Mail test additions: the API's TOP-LEVEL approvalRunEnabled (the gate
    // of "Find items" / "Real run"), kept only when it is a boolean - an
    // older API has none and the screen then hides the additions. Never
    // read from flags: the API's flags snapshot is pinned and has no such
    // key.
    ...(typeof body.approvalRunEnabled === "boolean"
      ? { approvalRunEnabled: body.approvalRunEnabled }
      : {}),
  };
}

// The samples as the screen reads them: the five lists always arrays.
export function normalizeMailTestSamples(raw: unknown): MailTestSamplesDto {
  const body = (
    raw !== null && typeof raw === "object" ? raw : {}
  ) as Record<string, unknown>;
  const list = <T,>(value: unknown): T[] =>
    Array.isArray(value)
      ? (value.filter((v) => v !== null && typeof v === "object") as T[])
      : [];
  const vendor = (
    body.vendor !== null && typeof body.vendor === "object" ? body.vendor : {}
  ) as Record<string, unknown>;
  return {
    ...(body as unknown as MailTestSamplesDto),
    vendor: {
      ...(vendor as unknown as MailTestSamplesDto["vendor"]),
      id: int(vendor.id) ?? 0,
      name: text(vendor.name) ?? "",
      navNumber: text(vendor.navNumber),
    },
    customers: list(body.customers),
    users: list(body.users),
    logos: list(body.logos),
    designs: list(body.designs),
    orders: list(body.orders),
  };
}

const ITEM_KINDS: ReadonlyArray<MailTestItemKind> = ["logo", "design"];
const APPROVAL_STATES: ReadonlyArray<MailTestApprovalState> = [
  "pendingVendor",
  "pendingCustomer",
  "approved",
  "rejected",
  "other",
];

function normalizeItemKind(value: unknown): MailTestItemKind | undefined {
  const kind = (text(value) ?? "").trim().toLowerCase();
  return ITEM_KINDS.find((k) => k === kind);
}

// The API's approvalState in any casing; "other" for a value this build
// does not know (never a crash on a newer API).
export function normalizeMailTestApprovalState(value: unknown): MailTestApprovalState {
  const raw = (text(value) ?? "").trim().toLowerCase();
  return APPROVAL_STATES.find((s) => s.toLowerCase() === raw) ?? "other";
}

// The raw JSON of GET ops/mail-test/items made safe for the finder: items
// an array (missing -> []), every row with a known kind and a finite id
// (others dropped), strings nullable, approvalState whitelisted, global
// only on true, truncated only on true.
export function normalizeMailTestItems(raw: unknown): MailTestItemsDto {
  const body = (
    raw !== null && typeof raw === "object" ? raw : {}
  ) as Record<string, unknown>;
  const items = (Array.isArray(body.items) ? body.items : [])
    .filter((entry) => entry !== null && typeof entry === "object")
    .map((entry): MailTestItemDto | null => {
      const r = entry as Record<string, unknown>;
      const kind = normalizeItemKind(r.kind);
      const id = int(r.id);
      if (kind === undefined || id === null) return null;
      return {
        kind,
        id,
        serial: text(r.serial),
        mul: text(r.mul),
        name: text(r.name) ?? "",
        customerOrgId: int(r.customerOrgId),
        customerName: text(r.customerName),
        createdBy: text(r.createdBy),
        createdAtUtc: text(r.createdAtUtc),
        approvalState: normalizeMailTestApprovalState(r.approvalState),
        global: flag(r.global),
      };
    })
    .filter((item): item is MailTestItemDto => item !== null);
  return { items, truncated: flag(body.truncated) };
}

// The raw JSON of POST ops/mail-test/approval-run made safe for the panel:
// the two lists arrays (a row without a known kind / finite id, or without
// an e-mail, is dropped), numbers nullable, wakeId / enqueuedAtUtc present
// ONLY when the API sent them (a dry run normalises without the keys),
// logUrl a string or null.
export function normalizeMailTestApprovalRunResult(
  raw: unknown,
): MailTestApprovalRunResultDto {
  const body = (
    raw !== null && typeof raw === "object" ? raw : {}
  ) as Record<string, unknown>;
  const items = (Array.isArray(body.items) ? body.items : [])
    .filter((entry) => entry !== null && typeof entry === "object")
    .map((entry) => {
      const r = entry as Record<string, unknown>;
      const kind = normalizeItemKind(r.kind);
      const id = int(r.id);
      if (kind === undefined || id === null) return null;
      return { kind, id, accepted: flag(r.accepted), reason: text(r.reason) };
    })
    .filter((x): x is MailTestApprovalRunResultDto["items"][number] => x !== null);
  const recipients = (Array.isArray(body.recipients) ? body.recipients : [])
    .filter((entry) => entry !== null && typeof entry === "object")
    .map((entry) => {
      const r = entry as Record<string, unknown>;
      const email = (text(r.email) ?? "").trim();
      return email === "" ? null : { email, allowlisted: flag(r.allowlisted) };
    })
    .filter((x): x is MailTestApprovalRunResultDto["recipients"][number] => x !== null);
  const logUrl = (text(body.logUrl) ?? "").trim();
  return {
    dryRun: flag(body.dryRun),
    direction: (text(body.direction) ?? "").trim().toLowerCase(),
    source: (text(body.source) ?? "").trim().toLowerCase(),
    vendorOrgId: int(body.vendorOrgId),
    customerOrgId: int(body.customerOrgId),
    items,
    recipients,
    bundleMinutes: int(body.bundleMinutes),
    ...optionalInt("wakeId", body.wakeId),
    ...optionalText("enqueuedAtUtc", body.enqueuedAtUtc),
    logUrl: logUrl === "" ? null : logUrl,
  };
}

export const MAIL_TEST_ROOT_KEY = "mailTest";
export const MAIL_TEST_SEND_KEY = [MAIL_TEST_ROOT_KEY, "send"];
export const MAIL_TEST_APPROVAL_RUN_KEY = [MAIL_TEST_ROOT_KEY, "approvalRun"];
const STALE_MS = 5 * 60 * 1000;

// null (not undefined) for an absent part: undefined segments are dropped
// by react-query's key hashing, which would collide two searches.
export function mailTestItemsKey(query: MailTestItemsQuery | undefined): unknown[] {
  return [
    MAIL_TEST_ROOT_KEY,
    "items",
    query?.vendorOrgId ?? -1,
    query?.kind ?? null,
    (query?.q ?? "").trim(),
    query?.pendingOnly === false ? false : true,
    query?.take ?? null,
  ];
}

export function useMailTestRepository(): MailTestRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  function useTemplates(options?: {
    enabled?: boolean;
  }): Resource<MailTestCatalogDto> {
    return useQuery(
      [MAIL_TEST_ROOT_KEY, "templates"],
      async () =>
        normalizeMailTestCatalog(await apiClient.getMailTestTemplates()),
      {
        staleTime: STALE_MS,
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchInterval: false,
        enabled: options?.enabled ?? true,
      },
    );
  }

  function useSamples(
    vendorOrgId: number | undefined,
    options?: { take?: number; enabled?: boolean },
  ): Resource<MailTestSamplesDto> {
    return useQuery(
      [MAIL_TEST_ROOT_KEY, "samples", vendorOrgId ?? -1, options?.take ?? null],
      async () =>
        normalizeMailTestSamples(
          await apiClient.getMailTestSamples(
            vendorOrgId as number,
            options?.take,
          ),
        ),
      {
        staleTime: STALE_MS,
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchInterval: false,
        enabled: vendorOrgId !== undefined && (options?.enabled ?? true),
      },
    );
  }

  async function refresh(vendorOrgId?: number): Promise<void> {
    // Other vendors' samples have no observer: dropped (no call now, ONE
    // call if that vendor is chosen again). The catalogue and the chosen
    // vendor's samples are refetched in place (refetchType "active").
    queryClient.removeQueries({
      queryKey: [MAIL_TEST_ROOT_KEY, "samples"],
      type: "inactive",
    });
    // "Find items" results of earlier searches / other vendors likewise
    // (no observer = no call); the search on screen is re-read in place.
    queryClient.removeQueries({
      queryKey: [MAIL_TEST_ROOT_KEY, "items"],
      type: "inactive",
    });
    await Promise.all([
      queryClient.invalidateQueries([MAIL_TEST_ROOT_KEY, "templates"]),
      ...(vendorOrgId === undefined
        ? []
        : [
            queryClient.invalidateQueries([
              MAIL_TEST_ROOT_KEY,
              "samples",
              vendorOrgId,
            ]),
            queryClient.invalidateQueries([
              MAIL_TEST_ROOT_KEY,
              "items",
              vendorOrgId,
            ]),
          ]),
    ]);
  }

  function useSend(): MailTestSend {
    const inFlight = useRef<Promise<MailTestSendResultDto> | undefined>(
      undefined,
    );
    const mutation = useMutation<
      MailTestSendResultDto,
      unknown,
      MailTestSendRequest
    >({
      mutationKey: MAIL_TEST_SEND_KEY,
      mutationFn: async (body) =>
        normalizeMailTestSendResult(await apiClient.sendMailTest(body)),
      // No onSuccess: a test mail changes nothing, nothing is invalidated.
    });
    const isAnyRunning = useIsMutating({ mutationKey: MAIL_TEST_SEND_KEY }) > 0;

    function send(body: MailTestSendRequest): Promise<MailTestSendResultDto> {
      // The ref is set synchronously: react-query flips isLoading only after
      // a microtask, so two clicks in the same tick would otherwise both
      // pass a state-based guard - and send two test mails.
      if (inFlight.current) return inFlight.current;
      const run = mutation.mutateAsync(body).finally(() => {
        inFlight.current = undefined;
      });
      inFlight.current = run;
      return run;
    }

    return {
      send,
      isLoading: mutation.isLoading,
      isAnyRunning,
      data: mutation.data,
      error: mutation.error,
      variables: mutation.variables,
      reset: mutation.reset,
    };
  }

  function useItems(
    query: MailTestItemsQuery | undefined,
    options?: { enabled?: boolean },
  ): Resource<MailTestItemsDto> {
    return useQuery(
      mailTestItemsKey(query),
      async () =>
        normalizeMailTestItems(
          await apiClient.getMailTestItems(query as MailTestItemsQuery),
        ),
      {
        staleTime: STALE_MS,
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchInterval: false,
        // The previous search's rows stay visible while the next one loads
        // (no empty flash between two lists).
        keepPreviousData: true,
        enabled: query !== undefined && (options?.enabled ?? true),
      },
    );
  }

  function useApprovalRun(): MailTestApprovalRun {
    const inFlight = useRef<Promise<MailTestApprovalRunResultDto> | undefined>(
      undefined,
    );
    const mutation = useMutation<
      MailTestApprovalRunResultDto,
      unknown,
      MailTestApprovalRunRequest
    >({
      mutationKey: MAIL_TEST_APPROVAL_RUN_KEY,
      mutationFn: async (body) =>
        normalizeMailTestApprovalRunResult(
          await apiClient.runMailTestApprovalRun(body),
        ),
      // No onSuccess: the panel polls the recipient org's log itself (a
      // bounded number of explicit refetches), nothing is invalidated.
    });

    function run(
      body: MailTestApprovalRunRequest,
    ): Promise<MailTestApprovalRunResultDto> {
      // Set synchronously, as in useSend: two clicks in one tick must not
      // queue two wakes.
      if (inFlight.current) return inFlight.current;
      const running = mutation.mutateAsync(body).finally(() => {
        inFlight.current = undefined;
      });
      inFlight.current = running;
      return running;
    }

    return {
      run,
      isLoading: mutation.isLoading,
      data: mutation.data,
      error: mutation.error,
      variables: mutation.variables,
      reset: mutation.reset,
    };
  }

  return { useTemplates, useSamples, refresh, useSend, useItems, useApprovalRun };
}
